import React from "react";
import PropTypes from "prop-types";
import { expertUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import { useLastMessageSentAt } from "../../source_directory/LastMessagesContext/LastMessagesContext";
import formatLastMessageDate from "../../source_directory/formatLastMessageDate";
import { initials } from "../../source_directory/utils";
import { SourceBadgeList } from "../../source_directory/SourceBadges";
import CardEmptyBody from "../../source_directory/CardEmptyBody";
import HighlightAttribute, { attributeMatched } from "../../source_directory/HighlightAttribute";
import ExpertJobLine from "./ExpertJobLine";
import isOwnExpert from "./viewerGating";

function ExpertCardGrid({ hit }) {
  const job = hit.jobs?.[0];
  const headlineMatched = attributeMatched(hit, "headline");
  const canMessage = !!globalThis.Qwoted?.CURRENT_USER_ID && !isOwnExpert(hit);
  const lastMessageDate = formatLastMessageDate(useLastMessageSentAt("Id::PromoEntity::Expert", hit.id));

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 4",
        display: "grid",
        gridTemplateRows: "subgrid",
        borderRadius: 14,
        border: "1px solid #d2d3d4",
        boxShadow: "0px 1px 1px rgba(15,20,36,0.04)",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "20px 20px 0" }}>
        {hit.profile_image_url ? (
          <img
            src={hit.profile_image_url}
            alt={hit.name}
            style={{ width: 48, height: 48, borderRadius: 24, objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              background: "#f5f9ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 700,
              fontSize: 15,
              color: "#533dd1",
            }}
          >
            {initials(hit.name)}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 19, lineHeight: 1.25, color: "#212528", letterSpacing: "-0.19px" }}>
            <a href={expertUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "inherit" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {(headlineMatched || job) && (
            <div style={{ fontSize: 14, color: "#4d5357", marginTop: 2 }}>
              <ExpertJobLine hit={hit} job={job} headlineMatched={headlineMatched} />
            </div>
          )}
          <SourceBadgeList
            types={[hit.frequent_source && "frequent_source", hit.quick_to_respond && "fast_to_respond"]}
          />
        </div>
        <SavedGroupsBookmark
          sourceableType="Id::PromoEntity::Expert"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>

      {/* Row 2: Separator — full-width (no padding; overflow:hidden on card clips to border-radius) */}
      <div style={{ height: 1, backgroundColor: "#e9e9e9" }} />

      {/* Row 3: Content */}
      <div style={{ padding: "0px 20px", display: "flex", flexDirection: "column" }}>
        {hit.location_string && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#212528" }}>
            <LocationMarkerIcon size={14} />
            {hit.location_string}
          </div>
        )}

        {hit.description && (
          <p style={{ fontSize: 14, color: "#212528", marginTop: 8, marginBottom: 0, lineHeight: "21px" }}>
            <HighlightAttribute
              hit={hit}
              attribute="description"
              snippet
              fallback={hit.description.length > 180 ? `${hit.description.slice(0, 180)}…` : hit.description}
            />
          </p>
        )}

        {hit.latest_quote && (
          <blockquote
            style={{
              margin: "12px 0 0",
              paddingLeft: 12,
              borderLeft: "3px solid #533dd1",
              fontSize: 14,
              fontStyle: "italic",
              color: "#4d5357",
              lineHeight: "21px",
            }}
          >
            {hit.latest_quote.length > 180 ? `${hit.latest_quote.slice(0, 180)}…` : hit.latest_quote}
          </blockquote>
        )}

        {!hit.description && !hit.latest_quote && <CardEmptyBody message="No bio or quote, yet." />}
      </div>

      {/* Row 4: CTA — always rendered to maintain 4-child count for subgrid */}
      {canMessage ? (
        <div style={{ padding: "14px 20px 20px" }}>
          <MessageButton
            promoEntityId={hit.id}
            promoEntityType="Id::PromoEntity::Expert"
            buttonLabel="Start a Conversation"
            extraButtonClasses="w-100 mb-0"
            buttonStyle={{ fontSize: 14 }}
            openInboxThread
          />
          {lastMessageDate && (
            <div style={{ textAlign: "center", fontSize: 12, lineHeight: "17.4px", color: "#4d5357", marginTop: 10 }}>
              Last message sent {lastMessageDate}
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

ExpertCardGrid.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    slug: PropTypes.string,
    profile_image_url: PropTypes.string,
    jobs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        company: PropTypes.string,
        company_slug: PropTypes.string,
      }),
    ),
    frequent_source: PropTypes.bool,
    quick_to_respond: PropTypes.bool,
    location_string: PropTypes.string,
    headline: PropTypes.string,
    description: PropTypes.string,
    latest_quote: PropTypes.string,
  }).isRequired,
};

export default ExpertCardGrid;
